import { AvatarRootProps } from "@chakra-ui/react/avatar";
import { Box } from "@chakra-ui/react/box";
import { Float } from "@chakra-ui/react/float";
import { Icon } from "@chakra-ui/react/icon";
import { Avatar } from "components/ui/avatar";
import { Tooltip } from "components/ui/tooltip";
import { ConversationChannel } from "entities/domain/conversations/conversation-domain";
import React from "react";
import { useAppSelector } from "redux/hooks";
import { getChannelIconElement } from "util/constants";

type FloatPlacement = "top-start" | "top-end" | "bottom-start" | "bottom-end";

type ProfileAvatarProps = {
  name: string;
  channelType?: ConversationChannel | string;
  profilePicture?: string;
  channelPlacement?: FloatPlacement;
  onClick?: () => void;
  onChannelClick?: (e: React.MouseEvent) => void;
  channelTooltip?: string;
  avatarStyle?: AvatarRootProps;
  topStart?: React.ReactNode;
  topEnd?: React.ReactNode;
  bottomStart?: React.ReactNode;
  bottomEnd?: React.ReactNode;
};

const ProfileAvatar = ({
  channelType,
  channelPlacement = "bottom-end",
  name,
  profilePicture = "",
  onClick,
  onChannelClick,
  channelTooltip,
  avatarStyle,
  topStart,
  topEnd,
  bottomStart,
  bottomEnd,
}: ProfileAvatarProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);

  const slots: Array<[FloatPlacement, React.ReactNode]> = [
    ["top-start", topStart],
    ["top-end", topEnd],
    ["bottom-start", bottomStart],
    ["bottom-end", bottomEnd],
  ];

  return (
    <Avatar
      key={`${name}-${channelType}`}
      size="sm"
      src={profilePicture}
      name={name && !name.startsWith("+") ? name : undefined}
      onClick={onClick}
      {...avatarStyle}
    >
      {channelType ? (
        <Float placement={channelPlacement} offsetY="1">
          <Tooltip
            content={channelTooltip}
            positioning={{ placement: "top" }}
            disabled={!channelTooltip}
          >
            <Box
              boxSize={4}
              background="transparent"
              border="none"
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor={onChannelClick ? "pointer" : undefined}
              onClick={onChannelClick}
              css={
                onChannelClick
                  ? {
                      transition: "transform 0.15s ease-in-out",
                      "&:hover": { transform: "scale(1.3)" },
                    }
                  : undefined
              }
            >
              <Icon
                as={getChannelIconElement(channelType)}
                boxSize="100%"
                color={{
                  _light: `${colorScheme}.500`,
                  _dark: `${colorScheme}.200`,
                }}
              />
            </Box>
          </Tooltip>
        </Float>
      ) : null}
      {slots.map(([placement, node]) =>
        node ? (
          <Float key={placement} placement={placement}>
            {node}
          </Float>
        ) : null
      )}
    </Avatar>
  );
};

export default ProfileAvatar;
